// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../hooks';
import { toast } from 'react-toastify';
import { getSessionUserEmail } from '../config/common';
import * as productListAction from '../Action/Product_Engagement/Product_List_Action';
import * as createEngagementAction from '../Action/Product_Engagement/Create_Engagement_Action';
import Loader from './Loader';

// Matches the Product_Engagement__c.Customer_Comment__c Long Text Area length in Salesforce.
// The API/Apex also truncates to this, but we cap entry here so the field never overflows.
const COMMENT_MAX_LENGTH = 32768;

const DiscoverAddons = () => {
    const dispatch = useAppDispatch();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [engagingProductId, setEngagingProductId] = useState(null);
    const [commentFor, setCommentFor] = useState(null);   // product awaiting a comment, or null
    const [commentText, setCommentText] = useState('');
    const hasFetched = useRef();

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProducts = () => {
        setIsLoading(true);
        const param = { email: getSessionUserEmail() };
        dispatch(productListAction.product_list({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                setProducts(response.data?.products || []);
            })
            .catch((error) => {
                setIsLoading(false);
                toast.error(error.message);
            });
    };

    const openComment = (product) => {
        setCommentText('');
        setCommentFor(product);
    };

    const closeComment = () => {
        if (engagingProductId) return; // don't dismiss mid-submit
        setCommentFor(null);
        setCommentText('');
    };

    const submitEngagement = () => {
        if (!commentFor) return;
        const productId = commentFor.Id;
        setEngagingProductId(productId);
        const param = {
            email: getSessionUserEmail(),
            productId: productId,
            comment: commentText,
        };
        dispatch(createEngagementAction.create_product_engagement({ data: JSON.stringify(param) }))
            .then((response) => {
                setEngagingProductId(null);
                if (response.status !== 1) {
                    toast.error(response.message);
                    return;
                }
                const newStatus = response.data?.status || 'New';
                toast.success(response.message || 'Interest recorded successfully!');
                setProducts((prev) =>
                    prev.map((p) => (p.Id === productId ? { ...p, status: newStatus } : p)),
                );
                setCommentFor(null);
                setCommentText('');
            })
            .catch((error) => {
                setEngagingProductId(null);
                toast.error(error.message);
            });
    };

    const isGtmProduct = (product) =>
        product.PLG_Status__c === 'GTM' || product.status === 'GTM';

    const getStatusBadge = (product) => {
        if (product.status === 'Complete') {
            return (
                <span className="badge rounded-pill text-bg-success px-3 py-2 copilot-addons-status">
                    <i className="fa-solid fa-check-circle me-2" aria-hidden />
                    Subscribed
                </span>
            );
        }
        if (product.status === 'New') {
            return (
                <span className="badge rounded-pill text-bg-info px-3 py-2 copilot-addons-status">
                    <i className="fa-solid fa-clock me-2" aria-hidden />
                    We will be in touch
                </span>
            );
        }

        const isGtm = isGtmProduct(product);

        return (
            <div className="d-flex flex-column gap-2 align-items-start">
                {isGtm && (
                    <span className="badge rounded-pill text-bg-secondary px-3 py-2 copilot-addons-status">
                        <i className="fa-solid fa-hourglass-half me-2" aria-hidden />
                        Coming soon
                    </span>
                )}
                <button
                    type="button"
                    className="btn btn-primary login-form-submit btn-sm"
                    onClick={() => openComment(product)}
                    disabled={engagingProductId === product.Id}
                >
                    {engagingProductId === product.Id ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                            Processing…
                        </>
                    ) : (
                        <>
                            <i
                                className={`fa-solid ${isGtm ? 'fa-hand-pointer' : 'fa-cart-plus'} me-2`}
                                aria-hidden
                            />
                            {isGtm ? "I'm Interested" : 'Subscribe now'}
                        </>
                    )}
                </button>
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loader />}

            <div className="container-fluid py-4 px-3 copilot-changelog">
                {isLoading ? null : products.length === 0 ? (
                    <div
                        className="copilot-addons-empty text-center rounded-4 border border-secondary-subtle bg-body-tertiary py-5 px-3"
                        role="status"
                    >
                        <span
                            className="copilot-home-card-icon copilot-home-card-icon--addons d-inline-flex align-items-center justify-content-center mb-3"
                            aria-hidden
                        >
                            <i className="fa-solid fa-box-open" />
                        </span>
                        <p className="fw-semibold text-body mb-1">
                            No add-ons available right now
                        </p>
                        <p className="text-body-secondary small mb-0">
                            Check back later — new services appear here when available.
                        </p>
                    </div>
                ) : (
                    <div className="row g-3 copilot-addons-grid">
                        {products.map((product) => (
                            <div className="col-12" key={product.Id}>
                                <div className="copilot-changelog-card copilot-addons-product-card h-100 mb-0">
                                    <div className="copilot-changelog-card__accent" aria-hidden />
                                    <div className="copilot-changelog-card__body d-flex flex-column h-100">
                                        <h2 className="copilot-changelog-section-title">{product.Name}</h2>
                                        <p className="copilot-changelog-section-lead flex-grow-1 mb-0">
                                            {product.Description || '—'}
                                        </p>
                                        <div className="pt-3 mt-3 border-top border-secondary-subtle">
                                            {getStatusBadge(product)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {commentFor && (
                <div
                    className="modal fade show d-block copilot-addons-comment-modal"
                    tabIndex={-1}
                    role="dialog"
                    style={{ background: 'rgba(0,0,0,0.4)' }}
                    onClick={closeComment}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {isGtmProduct(commentFor) ? "I'm Interested" : 'Subscribe'} — {commentFor.Name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={closeComment}
                                    disabled={!!engagingProductId}
                                />
                            </div>
                            <div className="modal-body">
                                <label htmlFor="addon-comment" className="form-label">
                                    Comments <span className="text-body-secondary">(optional)</span>
                                </label>
                                <textarea
                                    id="addon-comment"
                                    className="form-control"
                                    rows={5}
                                    maxLength={COMMENT_MAX_LENGTH}
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder="Tell us anything that would help us prioritize or tailor this for you."
                                    disabled={!!engagingProductId}
                                />
                                <div className="form-text text-end">
                                    {commentText.length.toLocaleString()}/{COMMENT_MAX_LENGTH.toLocaleString()}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={closeComment}
                                    disabled={!!engagingProductId}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary login-form-submit"
                                    onClick={submitEngagement}
                                    disabled={!!engagingProductId}
                                >
                                    {engagingProductId ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Submitting…
                                        </>
                                    ) : isGtmProduct(commentFor) ? (
                                        'Submit interest'
                                    ) : (
                                        'Subscribe now'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DiscoverAddons;
